import { unwrapOr } from "@zetalabs/core";

export interface Ok<T> {
	success: true;
	value: T;
	error: null;
}

export interface Err<E = Error> {
	success: false;
	value: null;
	error: E;
}

export type Result<T, E = Error> = Ok<T> | Err<E>;
export type AsyncResult<T, E = Error> = Promise<Result<T, E>>;

export type ExtractSuccess<T> = T extends Result<infer U, any> ? U : never;

export function isOk<T, E = Error>(result: Result<T, E>): result is Ok<T> {
	return result.success;
}

export function isErr<T, E = Error>(result: Result<T, E>): result is Err<E> {
	return !result.success;
}

export function ok<T>(value: T): Ok<T> {
	return { success: true, value, error: null };
}

export function err<E = Error>(error: E): Err<E> {
	return { success: false, value: null, error };
}

export async function tryPromise<T>(
	fn: (...args: any[]) => Promise<T>,
	errorMessage?: string,
): Promise<Result<T>> {
	try {
		const value = await fn();
		return ok(value);
	} catch (error) {
		return err(
			unwrapOr(
				error,
				new Error(
					errorMessage || "Unknown error while executing in `tryPromise`",
				),
			),
		);
	}
}

export function tryFn<T>(
	fn: (...args: any[]) => T,
	errorMessage?: string,
): Result<T> {
	try {
		const value = fn();
		return ok(value);
	} catch (error) {
		return err(
			unwrapOr(
				error,
				new Error(errorMessage || "Unknown error while executing in `try`"),
			),
		);
	}
}
